import React from 'react';
import { Redirect, Route, Switch } from 'react-router-dom';

// Components
import HeaderFooterLayout from '~/app/layouts/HeaderFooterLayout';

// Utils
import PATHS from '~/app/utils/paths';

// Routes
import ReserveRouter from '~/app/routers/ReserveRouter';
import NewAccountRouter from '~/app/routers/NewAccountRouter';
import NewMyFFRouter from '~/app/routers/NewMyFFRouter';
import EventRouter from '~/app/routers/EventRouter';
import ReferralRouter from '~/app/routers/ReferralRouter';
import PrivateRoute from '~/app/containers/PrivateRoute';
import ReferralLandingLayout from '~/app/layouts/ReferralLandingLayout';
import FXReferralLandingLayout from '~/app/layouts/FXReferralLandingLayout';
import FXFleetAuthLayout from '~/app/layouts/FXFleetAuthLayout';
import FXFleetLayout from '~/app/layouts/FXFleetLayout';
import FFRobotBusinessAuthLayout from '~/app/layouts/FFRobotBusinessAuthLayout';
import FFRobotBusinessLayout from '~/app/layouts/FFRobotBusinessLayout';
import AEMyFFRouter from '~/app/routers/AEMyFFRouter';
import AEAccountRouter from '~/app/routers/AEAccountRouter';
import AEReserveRouter from '~/app/routers/AEReserveRouter';
import FXPreorderLayout from '~/app/layouts/FXPreorderLayout';
import EcoProductRouter from '~/app/routers/EcoProductRouter';
import FXFleetRouter from '~/app/routers/FXFleetRouter';
import FFRobotBusinessRouter from '~/app/routers/FFRobotBusinessRouter';
import { ENABLE_REFERRAL_PROGRAM, ENABLE_NEW_ECOSYSTEM_PRODUCT } from '~/app/settings';
import loadable from '@loadable/component';

const ReferralLandingPage = loadable(() => import(/* webpackChunkName: "ReferralLandingPage" */ '~/app/pages/ReferralPage'));
const FXReferralLandingPage = loadable(() => import(/* webpackChunkName: "FXReferralLandingPage" */ '~/app/pages/FXReferralProgram/FXReferralLanding'));
const FXSimplePreorderPage717 = loadable(() => import(/* webpackChunkName: "FXSimplePreorderPage717" */ '~/app/pages/FXSimplePreorderPage717'));

// FX Fleet Auth pages
const FXFleetLoginPage = loadable(() => import(/* webpackChunkName: "FXFleetLoginPage" */ '~/app/pages/FXFleetPage/Login/LoginPage'));
const FXFleetRegisterPage = loadable(() => import(/* webpackChunkName: "FXFleetRegisterPage" */ '~/app/pages/FXFleetPage/Login/RegisterPage'));
const DisconnectRemoteVehicleAccessPage = loadable(() => import(/* webpackChunkName: "DisconnectRemoteVehicleAccessPage" */ '~/app/pages/DisconnectRemoteVehicleAccessPage'));

// FF Robot Business Auth pages
const FFRobotBusinessLoginPage = loadable(() => import(/* webpackChunkName: "FFRobotBusinessLoginPage" */ '~/app/pages/FFRobotBusinessPage/Login/LoginPage'));
const FFRobotBusinessRegisterPage = loadable(() => import(/* webpackChunkName: "FFRobotBusinessRegisterPage" */ '~/app/pages/FFRobotBusinessPage/Login/RegisterPage'));

// OAuth page
const OAuthLoginPage = loadable(() => import(/* webpackChunkName: "OAuthLoginPage" */ '~/app/pages/OAuthPage'));
const OAuthRegisterPage = loadable(() => import(/* webpackChunkName: "OAuthRegisterPage" */ '~/app/pages/OAuthPage/RegisterPage'));
const OAuthLogoutPage = loadable(() => import(/* webpackChunkName: "OAuthLogoutPage" */ '~/app/pages/OAuthPage/LogoutPage'));

const ScaffoldContainer = () => {

  return (
    <Switch>
      {/* The new version referral landing page has its own header layout */}
      {ENABLE_REFERRAL_PROGRAM && (
        <Route path={`/:locale(us|cn)${PATHS.REFERRAL}`} >
          {
            ({ match }) => (
              <ReferralLandingLayout>
                <ReferralLandingPage referralCode={match?.params?.referralCode} />
              </ReferralLandingLayout>
            )
          }
        </Route>
      )}
      {/* FX referral landing page */}
      <Route path={`/:locale(us|cn)${PATHS.FX_REFERRAL}`} >
        {
          ({ match }) => (
            <FXReferralLandingLayout>
              <FXReferralLandingPage referralCode={match?.params?.referralCode} />
            </FXReferralLandingLayout>
          )
        }
      </Route>
      {/* FX Fleet Auth pages - outside HeaderFooterLayout for full-width styling */}
      <Route exact path={`/:locale(us)${PATHS.FX_FLEET_LOGIN}`}>
        <FXFleetAuthLayout>
          <FXFleetLoginPage />
        </FXFleetAuthLayout>
      </Route>
      <Route exact path={`/:locale(us)${PATHS.FX_FLEET_REGISTER}`}>
        <FXFleetAuthLayout>
          <FXFleetRegisterPage />
        </FXFleetAuthLayout>
      </Route>
      {/* FX Fleet main pages - use custom layout with "Faraday X Fleet" header, no standard menu/footer */}
      <Route path={`/:locale(us)${PATHS.FX_FLEET}`}>
        <FXFleetLayout>
          <FXFleetRouter />
        </FXFleetLayout>
      </Route>
      {/* FF Robot Business Auth pages - outside HeaderFooterLayout for full-width styling */}
      <Route exact path={`/:locale(us)${PATHS.FF_ROBOT_BUSINESS_LOGIN}`}>
        <FFRobotBusinessAuthLayout>
          <FFRobotBusinessLoginPage />
        </FFRobotBusinessAuthLayout>
      </Route>
      <Route exact path={`/:locale(us)${PATHS.FF_ROBOT_BUSINESS_REGISTER}`}>
        <FFRobotBusinessAuthLayout>
          <FFRobotBusinessRegisterPage />
        </FFRobotBusinessAuthLayout>
      </Route>
      {/* FF Robot Business main pages - use custom layout with "EAI-Robotics Business" header */}
      <Route path={`/:locale(us)${PATHS.FF_ROBOT_BUSINESS}`}>
        <FFRobotBusinessLayout>
          <FFRobotBusinessRouter />
        </FFRobotBusinessLayout>
      </Route>
      {/* OAuth login page - standalone, no HeaderFooterLayout */}
      <Route exact path={`/:locale(us)${PATHS.OAUTH_LOGIN}`}>
        <OAuthLoginPage />
      </Route>
      {/* OAuth register page - standalone, no HeaderFooterLayout */}
      <Route exact path={`/:locale(us)${PATHS.OAUTH_REGISTER}`}>
        <OAuthRegisterPage />
      </Route>
      {/* OAuth logout page - standalone, no HeaderFooterLayout */}
      <Route exact path={`/:locale(us)${PATHS.OAUTH_LOGOUT}`}>
        <OAuthLogoutPage />
      </Route>
      {/* FX Simple Preorder 717 - use custom layout for immersive full-width design */}
      <Route exact path="/:locale(us|cn)/advanceorder/fx">
        <FXPreorderLayout>
          <FXSimplePreorderPage717 modelCode='FX Super One' />
        </FXPreorderLayout>
      </Route>

      <HeaderFooterLayout>
        <Switch>
          <Route exact path={`/:locale(us|cn)${PATHS.DISCONNECT_REMOTE_VEHICLE_ACCESS}`} component={DisconnectRemoteVehicleAccessPage} />
          <PrivateRoute path={`/:locale(us|cn)${PATHS.MY_FF}`} component={NewMyFFRouter} />
          <Route path={`/:locale(us|cn)${PATHS.ACCOUNT}`} component={NewAccountRouter} />
          <Route path={`/:locale(us|cn)${PATHS.RESERVE}`} component={ReserveRouter} />
          
          {/* AE Routes */}
          <PrivateRoute path={`/:locale(ae_ar|ae_en)${PATHS.MY_FF}`} component={AEMyFFRouter} />
          <Route path={`/:locale(ae_ar|ae_en)${PATHS.ACCOUNT}`} component={AEAccountRouter} />
          <Route path={`/:locale(ae_ar|ae_en)${PATHS.RESERVE}`} component={AEReserveRouter} />
          
          <Route path={`/:locale(us|cn)${PATHS.BASE_EVENT}`} component={EventRouter} />
          <Route path={`/:locale(us|cn)${PATHS.REFERRAL}`} component={ReferralRouter} />
          {ENABLE_NEW_ECOSYSTEM_PRODUCT && (
            <Route path={`/:locale(us|cn)${PATHS.ECOSYSTEM_PRODUCT_PREORDER}`} component={EcoProductRouter} />
          )}
          <Redirect to="/:locale(us|cn|ae_ar|ae_en)/404" />
        </Switch>
      </HeaderFooterLayout>
    </Switch>
  );
};

export default ScaffoldContainer;
